import React, { useState, useEffect, useMemo } from 'react';
import Header from './components/Header';
import Footer from './components/Footer';
import ProductCard from './components/ProductCard';
import AIAssistant from './components/AIAssistant';
import { products } from './data';
import { brandContent, brandLogos } from './brandData';
import { Line } from './types';
import { Star, ArrowDown, CheckCircle2, Filter, ThumbsUp, HelpCircle, Ruler, Search } from 'lucide-react';

const App: React.FC = () => {
  const [activeBrand, setActiveBrand] = useState('Pampers');
  const [activeTab, setActiveTab] = useState<Line | string | 'Todos'>('Todos');
  const [activeSize, setActiveSize] = useState<string | 'Todos'>('Todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [isHighlighting, setIsHighlighting] = useState(false);

  useEffect(() => {
    document.title = `FraldaTech | Ofertas ${activeBrand} - Guia e Comparativo`;
  }, [activeBrand]);

  const trackEvent = (eventName: string, params: any) => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', eventName, {
        ...params,
        send_to: 'G-X6K3H3B46M'
      });
    }
  };

  const handleBrandChange = (brand: string) => {
    setActiveBrand(brand);
    setActiveTab('Todos'); 
    setActiveSize('Todos');
    setSearchTerm(''); 
    scrollToSection('home');
    trackEvent('click_internal', { type: 'brand_change', brand: brand });
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    if (term) {
       scrollToSection('categorias'); 
       trackEvent('search', { search_term: term });
    }
  };

  const scrollToSection = (id: string) => {
    setTimeout(() => {
      const section = document.getElementById(id);
      if (section) {
        const headerOffset = 135;
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = section.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - headerOffset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });

        if (id === 'categorias' || id === 'ofertas') {
          setIsHighlighting(true);
          setTimeout(() => setIsHighlighting(false), 1500);
        }
      }
    }, 50);
  };

  const getBrandColor = (brand: string) => {
    switch (brand) {
      case 'Huggies': return 'text-red-600';
      case 'Babysec': return 'text-cyan-600';
      case 'Mamypoko': return 'text-blue-700';
      default: return 'text-store-purple';
    }
  };

  const getBrandBg = (brand: string) => {
    switch (brand) {
      case 'Huggies': return 'bg-red-600 hover:bg-red-700';
      case 'Babysec': return 'bg-cyan-600 hover:bg-cyan-700';
      case 'Mamypoko': return 'bg-blue-700 hover:bg-blue-800';
      default: return 'bg-store-purple hover:bg-store-dark';
    }
  };

  const brandProducts = useMemo(() => products.filter(p => p.brand === activeBrand), [activeBrand]);

  const filteredProducts = useMemo(() => {
    const searchLower = searchTerm.trim().toLowerCase();
    const pool = searchTerm !== '' ? products : brandProducts;
    return pool.filter(product => {
      if (searchLower !== '') {
        const keywords = searchLower.split(/\s+/);
        const searchTarget = [product.brand, product.name, product.line, product.size, product.category].join(' ').toLowerCase();
        return keywords.every(kw => searchTarget.includes(kw));
      }
      if (searchTerm === '') {
        const matchesTab = activeTab === 'Todos' || product.line === activeTab;
        const matchesSize = activeSize === 'Todos' || product.size === activeSize;
        return matchesTab && matchesSize;
      }
      return true;
    });
  }, [brandProducts, searchTerm, activeTab, activeSize]);

  return (
    <div className="min-h-screen flex flex-col font-sans bg-transparent relative" id="home">
      <div className="fixed inset-0 z-0 w-full h-full pointer-events-none">
         <img 
           src="https://images.unsplash.com/photo-1519689680058-324335c77eba?auto=format&fit=crop&w=2070&q=80" 
           alt="Background" 
           className="w-full h-full object-cover opacity-100"
         />
         <div className="absolute inset-0 bg-white/40 backdrop-blur-[2px]"></div>
      </div>

      <div className="relative z-10 flex flex-col min-h-screen w-full">
        <Header 
          onBrandChange={handleBrandChange} 
          currentBrand={activeBrand} 
          onSearch={handleSearch}
          onSizeChange={setActiveSize}
          onLineChange={setActiveTab}
          activeSize={activeSize}
          activeLine={activeTab}
        />
        
        <main className="flex-grow">
          {/* HERO REDUZIDO PARA CONVERSÃO RÁPIDA */}
          <section className="relative overflow-hidden min-h-[25vh] md:min-h-[60vh] flex items-center py-6 md:py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
              <div className="text-left bg-white/95 backdrop-blur-xl p-5 md:p-12 rounded-[1.5rem] md:rounded-[2.5rem] shadow-2xl border border-white/60">
                  <div className="flex items-center gap-2 mb-2 md:mb-4">
                    <span className="bg-store-accent text-store-dark px-2 py-0.5 rounded-full text-[9px] md:text-[10px] font-black uppercase tracking-widest shadow-sm">
                      Economia Verificada
                    </span>
                  </div>
                  
                  <h1 className="text-2xl sm:text-4xl md:text-6xl font-black text-gray-900 mb-2 md:mb-4 leading-none tracking-tighter">
                    Compare <span className={getBrandColor(activeBrand)}>Ofertas</span> em Segundos.
                  </h1>
                  
                  <p className="text-[11px] md:text-xl text-gray-600 mb-4 md:mb-8 leading-relaxed font-bold max-w-lg">
                    Encontramos os pacotes mais econômicos. Clique em VER PREÇO e compre com segurança na <strong className="text-orange-600">Amazon Brasil</strong>.
                  </p>
                  
                  <div className="flex flex-wrap gap-2.5 md:gap-4">
                    <button 
                       onClick={() => scrollToSection('categorias')}
                       className={`${getBrandBg(activeBrand)} text-white px-6 py-3 md:px-10 md:py-4 rounded-xl md:rounded-2xl font-black text-sm md:text-xl shadow-xl hover:scale-105 transition-all flex items-center gap-2`}
                    >
                      VER OFERTAS AGORA
                    </button>
                    <div className="hidden md:flex items-center gap-3 px-4 py-2 bg-gray-50 rounded-xl border border-gray-100">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-ping"></div>
                      <span className="text-[10px] font-black text-gray-500 uppercase tracking-wider">Monitoramento em Tempo Real</span>
                    </div>
                  </div>
              </div>

              {/* Sidebar Desktop - Trust Elements */}
              <div className="hidden lg:grid grid-cols-1 gap-4">
                 <div className="bg-white/80 backdrop-blur-md p-6 rounded-3xl border border-white/50 flex items-center gap-4 hover:shadow-lg transition-all transform hover:-translate-x-2">
                    <div className="bg-store-purple text-white p-3.5 rounded-2xl shadow-lg"><Star fill="white" size={24}/></div>
                    <div>
                      <h3 className="font-black text-gray-800">Curadoria Técnica</h3>
                      <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">Análise de Absorção e Custo</p>
                    </div>
                 </div>
                 <div className="bg-white/80 backdrop-blur-md p-6 rounded-3xl border border-white/50 flex items-center gap-4 hover:shadow-lg transition-all transform hover:-translate-x-2">
                    <div className="bg-store-accent text-store-dark p-3.5 rounded-2xl shadow-lg"><Search size={24}/></div>
                    <div>
                      <h3 className="font-black text-gray-800">Redirecionamento Seguro</h3>
                      <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">Links Verificados na Amazon</p>
                    </div>
                 </div>
              </div>
            </div>
          </section>

          {/* Catálogo de Ofertas */}
          <section id="categorias" className={`py-10 md:py-20 bg-gray-50/50 backdrop-blur-sm border-t border-gray-100 transition-colors duration-500 ${isHighlighting ? 'highlight-flash' : ''}`}>
            <div className="max-w-7xl mx-auto px-4">
              <div className="flex flex-col md:flex-row justify-between items-center mb-8 md:mb-14 gap-4 bg-white p-6 md:p-10 rounded-[1.5rem] md:rounded-[3rem] shadow-sm border border-gray-100">
                  <div className="text-center md:text-left">
                      <span className="text-store-purple font-black tracking-[0.2em] uppercase text-[10px] mb-2 block">Ofertas Verificadas</span>
                      <h2 className="text-2xl md:text-5xl font-black text-gray-900 leading-none">
                          {searchTerm !== '' ? (
                            <span>Resultado para <span className="text-store-purple">"{searchTerm}"</span></span>
                          ) : (
                            <span>Melhores Pacotes <span className={getBrandColor(activeBrand)}>{activeBrand}</span></span>
                          )}
                      </h2>
                  </div>
                  <div className="flex items-center gap-2.5 bg-green-50 px-4 py-2 rounded-2xl border border-green-100">
                    <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-[10px] font-black text-green-700 uppercase tracking-wider">Conectado com Amazon Brasil</span>
                  </div>
              </div>

              {/* Grid de Produtos */}
              <div id="ofertas" className="grid grid-cols-2 lg:grid-cols-4 gap-3.5 md:gap-10 scroll-mt-32">
                {filteredProducts.map((product) => (
                  <ProductCard key={`${product.id}-${product.brand}`} product={product} />
                ))}
              </div>

              {filteredProducts.length === 0 && (
                  <div className="text-center py-20 bg-white/60 rounded-[2.5rem] border border-dashed border-gray-200 shadow-inner">
                      <p className="text-gray-400 font-black text-xl mb-4">Nenhuma oferta encontrada para sua busca.</p>
                      <button 
                        onClick={() => { setActiveTab('Todos'); setActiveSize('Todos'); setSearchTerm(''); }}
                        className="px-8 py-3 bg-store-purple text-white rounded-xl font-bold hover:shadow-lg transition-all"
                      >
                        RECOMEÇAR PESQUISA
                      </button>
                  </div>
              )}
            </div>
          </section>
        </main>
        
        <Footer onBrandChange={handleBrandChange} onScrollTo={scrollToSection} />
        <AIAssistant />
      </div>
    </div>
  );
};

export default App;